import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Search, MessageSquareWarning } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ConfirmDelete from "@/components/ConfirmDelete";
import { PeriodFilter, HRFilter, inPeriod, useHROptions } from "@/components/PeriodFilter";

const LEVELS = ["High", "Medium", "Low"];
const STATUSES = ["Pending", "Under Review", "Action Required", "Resolved", "Closed"];
const PENDING_REASONS = ["Management Approval Pending", "Information Required", "Department Response Pending", "Employee Response Pending", "Investigation Ongoing", "External Dependency", "Other"];
const CONFIRMATIONS = ["Not Required", "Confirmed Resolved", "Not Confirmed"];

const statusCls = (s) => ({ Pending: "rag-amber", "Under Review": "rag-amber", "Action Required": "rag-red", Resolved: "rag-green", Closed: "rag-green" }[s] || "rag-amber");
const levelCls = (l) => ({ High: "rag-red", Medium: "rag-amber", Low: "rag-green" }[l] || "rag-amber");

export default function Concerns() {
  const { user, branch } = useAuth();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const hrUsers = useHROptions(true);
  const [meta, setMeta] = useState({ concern_categories: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [q, setQ] = useState("");
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  const [hrSel, setHrSel] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fLevel, setFLevel] = useState("all");

  const load = () => api.get("/concerns").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => items.filter((c) => {
    if (branch !== "All" && c.branch !== branch) return false;
    if (!inPeriod(c.date_raised, range)) return false;
    if (hrSel !== "all" && c.hr_responsible_id !== hrSel) return false;
    if (fStatus !== "all" && c.status !== fStatus) return false;
    if (fLevel !== "all" && c.level !== fLevel) return false;
    if (q && !`${c.employee_name} ${c.concern_code} ${c.category} ${c.description}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, branch, range, hrSel, fStatus, fLevel, q]);

  const kpi = useMemo(() => ({
    total: filtered.length,
    pending: filtered.filter((c) => c.status === "Pending").length,
    review: filtered.filter((c) => c.status === "Under Review").length,
    action: filtered.filter((c) => c.status === "Action Required").length,
    closed: filtered.filter((c) => ["Resolved", "Closed"].includes(c.status)).length,
    overdue: filtered.filter((c) => c.overdue_days > 0).length,
    high: filtered.filter((c) => c.level === "High" && !["Resolved", "Closed"].includes(c.status)).length,
  }), [filtered]);

  const openNew = () => {
    setForm({ status: "Pending", level: "Medium", employee_confirmation: "Not Required", date_raised: new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };

  const openEdit = (c) => { setForm({ ...c }); setOpen(true); };

  const save = async () => {
    try {
      const { id, concern_code, employee_name, department, designation, branch: b, overdue_days, created_at, created_by, updated_at, updated_by, closed_by, closed_at, ...body } = form;
      if (id) {
        await api.patch(`/concerns/${id}`, body);
        toast.success("Concern updated");
      } else {
        await api.post("/concerns", body);
        toast.success("Concern logged");
      }
      setOpen(false); setForm({}); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/concerns/${id}`); toast.success("Concern deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const pendingish = ["Pending", "Under Review", "Action Required"].includes(form.status);
  const closedish = ["Resolved", "Closed"].includes(form.status);
  const selEmp = employees.find((e) => e.id === form.employee_id);

  return (
    <div className="space-y-6" data-testid="concerns-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Employee Concerns</div>
          <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1">Concerns Tracker</h1>
          <p className="text-slate-600 text-sm mt-1">Complaints and concerns raised to HR — with target closure dates and overdue alerts.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="add-concern-button">
          <Plus className="h-4 w-4 mr-1" /> Log concern
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2 bg-white border border-slate-200 rounded-lg p-3" data-testid="concern-kpis">
        {[["Total", kpi.total], ["Pending", kpi.pending], ["Under Review", kpi.review], ["Action Req.", kpi.action], ["Closed", kpi.closed], ["Overdue", kpi.overdue, "rose"], ["High Priority", kpi.high, "rose"]].map(([label, val, tone]) => (
          <div key={label} className="px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className={`font-heading text-xl font-bold ${tone === "rose" && val > 0 ? "text-[var(--yc-rose)]" : "text-[var(--yc-navy)]"}`}>{val}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-end flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search employee, ID, category…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="concern-search-input" />
        </div>
        <PeriodFilter preset={preset} onPreset={setPreset} range={range} onRange={setRange} testId="concern-period-filter" />
        <HRFilter value={hrSel} onChange={setHrSel} users={hrUsers} testId="concern-hr-filter" />
        <div className="w-40">
          <Label className="label-eyebrow">Status</Label>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="mt-1.5 h-9 bg-white" data-testid="concern-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-32">
          <Label className="label-eyebrow">Level</Label>
          <Select value={fLevel} onValueChange={setFLevel}>
            <SelectTrigger className="mt-1.5 h-9 bg-white" data-testid="concern-level-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All levels</SelectItem>{LEVELS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                <th className="data-cell">ID · Raised</th>
                <th className="data-cell">Employee</th>
                <th className="data-cell">Category</th>
                <th className="data-cell">Level</th>
                <th className="data-cell">HR</th>
                <th className="data-cell">Status</th>
                <th className="data-cell">Target</th>
                <th className="data-cell">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="data-cell text-center text-slate-500 py-10">No concerns match. Log one with &ldquo;Log concern&rdquo;.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 data-row" data-testid={`concern-row-${c.id}`}>
                  <td className="data-cell">
                    <div className="font-mono text-xs font-semibold text-[var(--yc-navy)]">{c.concern_code}</div>
                    <div className="text-xs text-slate-500">{c.date_raised}</div>
                  </td>
                  <td className="data-cell">
                    <div className="font-medium text-[var(--yc-navy)]">{c.employee_name}</div>
                    <div className="text-xs text-slate-500">{c.designation} · {c.department} · {c.branch}</div>
                  </td>
                  <td className="data-cell text-slate-600 text-xs">{c.category}</td>
                  <td className="data-cell"><span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${levelCls(c.level)}`}>{c.level}</span></td>
                  <td className="data-cell text-slate-600 text-xs">{c.hr_responsible}</td>
                  <td className="data-cell">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${statusCls(c.status)}`}>{c.status}</span>
                    {c.pending_reason && !["Resolved", "Closed"].includes(c.status) && <div className="text-[10px] text-slate-500 mt-1">{c.pending_reason}</div>}
                    {c.overdue_days > 0 && (
                      <div className="rag-red px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-bold mt-1 inline-block" data-testid={`concern-overdue-${c.id}`}>Overdue {c.overdue_days}d</div>
                    )}
                  </td>
                  <td className="data-cell text-slate-600 text-xs">{c.target_closure_date || "—"}{c.closure_date && <div className="text-emerald-600">Closed {c.closure_date}</div>}</td>
                  <td className="data-cell">
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => openEdit(c)} className="p-1.5 rounded-md text-slate-400 hover:text-[var(--yc-navy)] hover:bg-slate-100 transition-colors" title="Edit" data-testid={`edit-concern-${c.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {["super_admin", "hr_manager"].includes(user?.role) && (
                        <ConfirmDelete onConfirm={() => remove(c.id)} label={`concern ${c.concern_code}`} testId={`delete-concern-${c.id}`} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MessageSquareWarning className="h-4 w-4 text-[var(--yc-emerald)]" />
              {form.id ? `Edit concern ${form.concern_code}` : "Log employee concern"}
            </DialogTitle>
            <DialogDescription>Select the employee — department, designation and branch auto-fill. Keep descriptions short and factual.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="label-eyebrow">Employee</Label>
              <Select value={form.employee_id || ""} onValueChange={(v) => setForm({ ...form, employee_id: v })} disabled={!!form.id}>
                <SelectTrigger className="mt-1.5" data-testid="concern-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.designation} · {e.branch}</SelectItem>)}
                </SelectContent>
              </Select>
              {selEmp && !form.id && <div className="text-xs text-slate-500 mt-1">{selEmp.department} · {selEmp.designation} · {selEmp.branch} (auto-fetched)</div>}
            </div>
            <div>
              <Label className="label-eyebrow">Date raised</Label>
              <Input type="date" value={form.date_raised || ""} onChange={(e) => setForm({ ...form, date_raised: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">HR responsible</Label>
              <Select value={form.hr_responsible_id || ""} onValueChange={(v) => {
                const u = hrUsers.find((x) => x.id === v);
                setForm({ ...form, hr_responsible_id: v, hr_responsible: u?.name || "" });
              }}>
                <SelectTrigger className="mt-1.5" data-testid="concern-hr-select"><SelectValue placeholder="Assign HR" /></SelectTrigger>
                <SelectContent>{hrUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Category</Label>
              <Select value={form.category || ""} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="concern-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent className="max-h-64">{(meta.concern_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Concern level</Label>
              <Select value={form.level || "Medium"} onValueChange={(v) => setForm({ ...form, level: v })}>
                <SelectTrigger className="mt-1.5" data-testid="concern-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">What is the concern? (short &amp; factual)</Label>
              <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" placeholder='e.g., "Employee reported incorrect salary calculation for September."' data-testid="concern-description-input" />
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">Action taken / solution</Label>
              <Textarea rows={2} value={form.action_taken || ""} onChange={(e) => setForm({ ...form, action_taken: e.target.value })} className="mt-1.5" placeholder='e.g., "Attendance verified and payroll correction processed."' />
            </div>
            <div>
              <Label className="label-eyebrow">Status</Label>
              <Select value={form.status || "Pending"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="concern-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Target closure date</Label>
              <Input type="date" value={form.target_closure_date || ""} onChange={(e) => setForm({ ...form, target_closure_date: e.target.value })} className="mt-1.5" data-testid="concern-target-input" />
            </div>
            {pendingish && (
              <div className="col-span-2">
                <Label className="label-eyebrow">Pending reason</Label>
                <Select value={form.pending_reason || ""} onValueChange={(v) => setForm({ ...form, pending_reason: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="concern-pending-reason"><SelectValue placeholder="Why is it pending?" /></SelectTrigger>
                  <SelectContent>{PENDING_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {closedish && (
              <>
                <div>
                  <Label className="label-eyebrow">Closure date</Label>
                  <Input type="date" value={form.closure_date || ""} onChange={(e) => setForm({ ...form, closure_date: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className="label-eyebrow">Employee confirmation</Label>
                  <Select value={form.employee_confirmation || "Not Required"} onValueChange={(v) => setForm({ ...form, employee_confirmation: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{CONFIRMATIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="label-eyebrow">Final solution</Label>
                  <Input value={form.final_solution || ""} onChange={(e) => setForm({ ...form, final_solution: e.target.value })} className="mt-1.5" />
                </div>
                <div className="col-span-2">
                  <Label className="label-eyebrow">Closing remarks</Label>
                  <Input value={form.closing_remarks || ""} onChange={(e) => setForm({ ...form, closing_remarks: e.target.value })} className="mt-1.5" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="save-concern-button">{form.id ? "Save changes" : "Log concern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
